import { useEffect, useMemo, useRef, useState } from 'react';
import { CaretLeft, CaretRight } from '@carbon/react/icons';
import {
  InlineNotification,
  Loading,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
} from '@carbon/react';
import { EntityTitleAssets } from 'components/entity-title-assets';
import { useAtomValue } from 'jotai';
import { Anchor, Popup, useMap } from 'react-map-gl';
import classNames from 'classnames';

import style from './map.module.scss';
import { useTitleEntityAssetAtom } from 'components/entity-title-assets/atoms';
import { debounce } from 'lodash-es';

const INTEREST_TYPES = {
  WORKING: 'working',
  ROYALTY: 'royalty',
  SURFACE: 'surface',
  UNLEASED: 'unleased',
};

export const TitleEntityAssetsPopup = ({
  assets,
  latitude,
  longitude,
  closeButton,
  onClose,
}: {
  assets: Array<{ id: string; primary_name?: string; interest_type?: string }>;
  latitude: number;
  longitude: number;
  closeButton: boolean;
  onClose: () => void;
}) => {
  // State for selected interest types (checkboxes)
  const [selectedInterestTypes] = useState({
    [INTEREST_TYPES.WORKING]: true,
    [INTEREST_TYPES.ROYALTY]: true,
    [INTEREST_TYPES.SURFACE]: true,
    [INTEREST_TYPES.UNLEASED]: true,
  });

  // State for currently selected tab
  const [activeTab, setActiveTab] = useState(INTEREST_TYPES.WORKING);

  // Group assets by interest type for easier access
  const assetsByType = useMemo(() => {
    return assets.reduce<Record<string, typeof assets>>((acc, asset) => {
      const type = asset.interest_type?.toLowerCase() || 'unknown';
      if (!acc[type]) acc[type] = [];
      acc[type].push(asset);
      return acc;
    }, {});
  }, [assets]);

  // Count entities by interest type
  const entityCountsByType = useMemo(() => {
    return assets.reduce<Record<string, number>>((counts, asset) => {
      const type = asset.interest_type?.toLowerCase() || 'unknown';
      counts[type] = (counts[type] || 0) + 1;
      return counts;
    }, {});
  }, [assets]);

  // Get visible tabs based on selected interest types and available assets
  const visibleTabs = useMemo(() => {
    return Object.entries(INTEREST_TYPES)
      .filter(
        ([_, value]) =>
          selectedInterestTypes[value] && assetsByType[value]?.length > 0
      )
      .map(([key, value]) => ({
        key,
        value,
        count: entityCountsByType[value] || 0,
      }));
  }, [selectedInterestTypes, assetsByType, entityCountsByType]);

  // Set initial active tab based on visible tabs
  useEffect(() => {
    if (
      visibleTabs.length > 0 &&
      !visibleTabs.some((tab) => tab.value === activeTab)
    ) {
      setActiveTab(visibleTabs[0].value);
    }
  }, [visibleTabs, activeTab]);

  // Current entities for the active tab
  const currentTabEntities = assetsByType[activeTab] || [];

  // Navigation state within each tab
  const [currentIndexByTab, setCurrentIndexByTab] = useState({});

  // Get or initialize the current index for the active tab
  const getCurrentIndex = (tabType: string): number => {
    return (currentIndexByTab as Record<string, number>)[tabType] ?? 0;
  };

  // Current index for the active tab
  const currentIndex = getCurrentIndex(activeTab);

  // Current entity for the active tab
  const currentEntity = currentTabEntities[currentIndex] || null;

  // Resource URL for fetching entity assets
  const resourceUrl = useMemo(() => {
    if (currentEntity?.id) {
      return `/maps/tiles/entities/${currentEntity.id}/entity_title_assets?latitude=${latitude}&longitude=${longitude}`;
    }
    return undefined;
  }, [currentEntity?.id, latitude, longitude]);

  // Debounce resource URL to prevent excessive fetching
  const isFirstRender = useRef(true);
  const [debouncedUrl, setDebouncedUrl] = useState(resourceUrl);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setDebouncedUrl(resourceUrl);
    } else {
      const debouncedHandler = debounce(() => {
        setDebouncedUrl(resourceUrl);
      }, 400);

      debouncedHandler();

      return () => {
        debouncedHandler.cancel();
      };
    }
    return undefined;
  }, [resourceUrl, currentEntity?.id]);

  // Fetch entity assets
  const titleEntityAssetAtom = useTitleEntityAssetAtom(debouncedUrl);
  const { loading, data } = useAtomValue(titleEntityAssetAtom);

  const assetsFetcher = {
    loading,
    assets: data && !('error' in data) ? data : [],
    entityName: currentEntity?.primary_name || '',
    error: data && 'error' in data ? data.error : undefined,
  };

  // Handle tab change
  const handleTabChange = (newTabValue: string) => {
    if (newTabValue) {
      setActiveTab(newTabValue);
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (currentIndex < currentTabEntities.length - 1) {
      setCurrentIndexByTab({
        ...currentIndexByTab,
        [activeTab]: currentIndex + 1,
      });
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndexByTab({
        ...currentIndexByTab,
        [activeTab]: currentIndex - 1,
      });
    }
  };

  // Add conditional rendering for previous/next buttons
  const showPrevButton = currentIndex > 0;
  const showNextButton = currentIndex < currentTabEntities.length - 1;

  // Popup positioning
  const { current: map } = useMap();
  const [anchor, setAnchor] = useState<Anchor>('right');

  useEffect(() => {
    if (!map) return;

    const mapContainer = map.getContainer();
    const { width, height } = mapContainer.getBoundingClientRect();
    const point = map.project([longitude, latitude]);
    const popupWidth = 1000;
    const popupHeight = 300;
    const buffer = 10;
    const spaceOnRight = width - point.x - buffer;
    const spaceOnBottom = height - point.y - buffer;

    let horizontalAnchor: string;
    if (spaceOnRight < popupWidth) {
      horizontalAnchor = 'right';
    } else {
      horizontalAnchor = 'left';
    }

    let verticalAnchor: string;
    if (spaceOnBottom < popupHeight) {
      verticalAnchor = 'bottom';
    } else {
      verticalAnchor = 'top';
    }

    const finalAnchor: Anchor = verticalAnchor
      ? (`${verticalAnchor}-${horizontalAnchor}` as Anchor)
      : (horizontalAnchor as Anchor);

    setAnchor(finalAnchor);
  }, [map, longitude, latitude]);

  // Render entity content for the current tab
  const renderEntityContent = () => {
    if (!currentEntity) {
      return (
        <div className={style.noEntitiesMessage}>
          No entities available for this interest type.
        </div>
      );
    }

    return (
      <>
        <div className={style.entityHeader}>
          <div>
            <h3 className={style.entityTitle}>
              {currentEntity?.primary_name || 'No entities available'}
            </h3>
          </div>
          <div className={style.entityPagination}>
            <button
              className={classNames(
                style.paginationButton,
                showPrevButton ? style.visible : style.hidden
              )}
              onClick={handlePrevious}
              disabled={!showPrevButton}
            >
              <CaretLeft data-testid="caret-left-icon" />
            </button>
            <span>
              {currentTabEntities.length > 0
                ? `${currentIndex + 1} of ${currentTabEntities.length}`
                : '0'}{' '}
              Entities
            </span>
            <button
              className={classNames(
                style.paginationButton,
                showNextButton ? style.visible : style.hidden
              )}
              onClick={handleNext}
              disabled={!showNextButton}
            >
              <CaretRight data-testid="caret-right-icon" />
            </button>
          </div>
        </div>
        {assetsFetcher.loading && (
          <div className={style.titleEntityLoading}>
            <Loading
              withOverlay={false}
              description="Loading entity assets..."
            />
          </div>
        )}
        {!assetsFetcher.loading && assetsFetcher.error && (
          <div className={style.titleEntityErrorMessage}>
            <InlineNotification
              kind="error"
              title="Error loading entity assets"
              hideCloseButton={true}
            />
          </div>
        )}
        {!assetsFetcher.loading && !assetsFetcher.error && (
          <EntityTitleAssets
            assets={assetsFetcher.assets}
            activeTab={activeTab}
          />
        )}
      </>
    );
  };

  return (
    <Popup
      anchor={anchor}
      key={Number(closeButton)}
      closeButton={closeButton}
      closeOnClick={false}
      onClose={onClose}
      latitude={latitude}
      longitude={longitude}
      className={classNames(
        style.popup,
        style.titleEntityAssetsPopup,
        style.titleEntityAssetsPopupContainer
      )}
      maxWidth="1000px"
    >
      <div className={style.popupContainer}>
        {visibleTabs.length > 0 ? (
          <div className={style.tabsContainer}>
            <Tabs
              selectedIndex={visibleTabs.findIndex(
                (tab) => tab.value === activeTab
              )}
              onChange={({ selectedIndex }) =>
                handleTabChange(visibleTabs[selectedIndex]?.value)
              }
              aria-label="Interest Type Tabs"
            >
              <TabList contained>
                {visibleTabs.map(({ key, value }) => {
                  const label =
                    key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
                  return (
                    <Tab key={value} className={style.popupTab}>
                      {`${label}${
                        label !== 'Unleased' ? ' Interest' : ''
                      } (Title)`}
                    </Tab>
                  );
                })}
              </TabList>
              <TabPanels>
                {visibleTabs.map(({ value }) => (
                  <TabPanel key={value}>
                    <div className={style.tabContent}>
                      {renderEntityContent()}
                    </div>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </div>
        ) : (
          <div className={style.noTabsMessage}>
            No interest types selected or no data available. Please select at
            least one interest type to view entities.
          </div>
        )}
      </div>
    </Popup>
  );
};
